import type { GetStoreFn, AnySelector } from '../types';

import { createSelectorFlowFactory, SelectorFlowFactory } from './createSelectorFlowFactory';
import { MemoizeAutotrackSelectorOptions, memoizeAutotrackSelectorBuilder } from './memoizeAutotrackSelectorBuilder';

/**
 * Тип функции создания модульного селектора
 *
 * @typeParam SliceState - Стейт слайса
 */
export type CreateModulesSelector<SliceState> = <Selector extends AnySelector<SliceState>>(
    selector: Selector,
    options?: MemoizeAutotrackSelectorOptions
) => SelectorFlowFactory<Selector>;

/**
 * Билдер функции создания модульных селекторов для слайса
 *
 * @typeParam SliceState - Стейт слайса
 * @typeParam Name - Название слайса
 * @returns Функция для создания модульного селектора
 *
 * @internal
 */
export function buildCreateSelector<SliceState, Name extends string>({
    sliceName,
    getStore,
}: {
    /** Название слайса */
    sliceName: Name;
    /** Функция для получения стора */
    getStore: GetStoreFn;
}): CreateModulesSelector<SliceState> {
    type StoreSlice = { [key in Name]: SliceState };

    const memoizeSelector = memoizeAutotrackSelectorBuilder<StoreSlice, SliceState>({
        getStore,
        getSelectorState: storeState => storeState[sliceName],
    });

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    return ((selector, options) => {
        // Количество дополнительных параметров в селекторе
        const restArgsCount = Math.max(selector.length - 1, 0);

        // В функцию мемоизации пробрасываем селектор,
        // который вызывает создаваемый селектор только со стейтом текущего слайса
        const memoizedSelector = memoizeSelector(selector, {
            ...options,
            restArgsCount,
        });

        return createSelectorFlowFactory({
            getStore,
            getSelector: () => memoizedSelector,
        });
    }) as CreateModulesSelector<SliceState>;
}
